import { BehaviorSubject } from "rxjs";
import { TCarrinho, TCarrinhoAvulso, TCarrinhoItem, TFinalizarCarrinho, TItemAdicional } from "../types";
import api, { IDataRequest, IDataResponse, STATUS_CODE, apiNoLoading } from "src/framework/providers/api";
import { LocalStorage } from "src/framework/commons";
import { ArrayUtil } from "src/framework/commons/ArrayUtil";
import { notifierStore } from "src/framework/components/commons/dialogs/Notifier/store";
import { AppError } from "src/framework/exceptions/AppError";

export class CarrinhoStoreClass {
    private open$ = new BehaviorSubject<boolean>(false);
    private carrinho$ = new BehaviorSubject<TCarrinho | null>(null);
    private guid$ = new BehaviorSubject<string>("");
    private totalItens$ = new BehaviorSubject<number>(0);

    get open() {
        return this.open$.asObservable();
    }

    setOpen(open: boolean) {
        this.open$.next(open);
    }

    toggleOpen() {
        this.open$.next(!this.open$.getValue());
    }

    get carrinho() {
        return this.carrinho$.asObservable();
    }

    setCarrinho(carrinho: TCarrinho | null) {
        this.carrinho$.next(carrinho);
        this.totalItens$.next(
            (carrinho?.venda === 'avulso' 
                ? carrinho?.avulsos?.length 
                : carrinho?.itens?.length
            ) || 0
        );
    }

    getCarrinho() {
        return this.carrinho$.getValue();
    }

    getTotalItens() {
        return this.totalItens$.getValue();
    }

    get totalItens() {
        return this.totalItens$.asObservable();
    }

    validaQuantidadeMaxima(item: TCarrinhoItem): boolean {
        //validação:
        if(item.produto){
            if (item.produto.quantidade_maxima && item.quantidade > item.produto.quantidade_maxima) {
                notifierStore.setNotifier({
                    title: "Aviso!",
                    content: item?.produto?.quantidade_maxima ? `Não é possível adicionar mais que ${item?.produto?.quantidade_maxima} unidade(s) do kit escohido.` : `Não é possível adicionar kits.`,
                    onClose: () => { },
                    noTimeOut: true,
                })
                notifierStore.open();
                return false;
            }else if(item.produto.quantidade_minima && item.quantidade < item.produto.quantidade_minima){
                notifierStore.setNotifier({
                    title: "Aviso!",
                    content: item?.produto?.quantidade_minima ? `Não é possível adicionar menos que ${item?.produto?.quantidade_minima} unidade(s) do kit escolhido.` : `Não é possível adicionar kits.`,
                    onClose: () => { },
                    noTimeOut: true,
                })
                notifierStore.open();
                return false;
            }
        }
        return true;
    }

    validaQuantidadeMaximaAvulso(item: TCarrinhoAvulso, isAdmin: boolean): boolean {
        if (item?.componente && !isAdmin) {
            if (item?.componente?.quantidade_maxima_venda_avulso && item.quantidade > item.componente.quantidade_maxima_venda_avulso) {
                notifierStore.setNotifier({
                    title: "Aviso!",
                    content: `Não é possível adicionar mais que ${item.componente.quantidade_maxima_venda_avulso} unidade(s) do item escohido.`,
                    onClose: () => { },
                    noTimeOut: true,
                })
                notifierStore.open();
                return false;
            }
        }
        return true;
    }

    async addItem(item: TCarrinhoItem, increment?: boolean): Promise<void> {
        const carrinho = {...this.getCarrinho()};

        try {
            if (Object.keys(carrinho).length === 0 && carrinho.constructor === Object) {
                item.sequencia = 1;
                const quantidadeMinima = item?.produto?.quantidade_minima || 0;
                const _carrinho: TCarrinho = {
                    venda: "gerador",
                    itens: [{
                        produto_id: item.produto?.id,
                        quantidade: item.quantidade < quantidadeMinima ? quantidadeMinima : item.quantidade,
                        sequencia: 1,
                        itens_adicionais: item.itens_adicionais || [],
                    }],
                }

                const guid = await this.criarCarrinho(_carrinho);
                _carrinho.guid = guid;
                this.carrinho$.next(_carrinho); 
                this.setGuid(guid);
                const carrinhos = await this.obterCarrinhos();
                if (carrinhos?.length) {
                    this.setCarrinho({...carrinhos[0]});
                }
            } else if (carrinho.venda !== "gerador") {
                this.limpar();
                await this.addItem(item);
                return;
            } else {
                let removeIndex = -1
                const itensAdicionais = item.itens_adicionais?.map(i => i.componente_id) || [];
                const itemIndex = Number(carrinho?.itens?.findIndex((_item, index) => {
                    const _itensAdicionais = _item.itens_adicionais?.map(i => i.componente_id) || [];
                    const isSame = ArrayUtil.hasSameValues(itensAdicionais, _itensAdicionais)
                    if (!isSame) removeIndex = index;
                    return _item?.produto?.id === item.produto?.id && isSame;
                }));

                if (isNaN(itemIndex)) {
                    item.sequencia = 1;
                }

                if (removeIndex > -1) {
                    this.removeItem(removeIndex);
                    this.limpar();
                    await this.addItem(item);
                    return;
                }

                if (itemIndex === -1) {
                    item.sequencia = carrinho.itens?.length ? carrinho.itens.length + 1 : 1;
                }

                if (carrinho?.itens) {
                    if (carrinho.itens && itemIndex > -1) {

                        if (increment) {
                            carrinho.itens[itemIndex].quantidade += item.quantidade;
                        } else {
                            carrinho.itens[itemIndex].quantidade = item.quantidade;
                        }
                        await this.atualizarCarrinho(carrinho);
                        return
                    }
                }
                carrinho["itens"] = [item];
                await this.atualizarCarrinho(carrinho);
            }
        } catch(error) {
            if (error instanceof AppError) {
                error.openNotifier();
                return;
            }
        }
    }

    async addItemAvulso(item: TCarrinhoAvulso, increment?: boolean, estadoId?: string): Promise<void> {
        const carrinho = {...this.getCarrinho()};
        
        try {
            if (Object.keys(carrinho).length === 0 && carrinho.constructor === Object) {
                const _carrinho: TCarrinho = {
                    estado_id: estadoId,
                    venda: "avulso",
                    avulsos: [{
                        componente_id: item?.componente?.id,
                        quantidade: item.quantidade,
                        valor: item.valor,
                    }],
                }

                const guid = await this.criarCarrinho(_carrinho);
                _carrinho.guid = guid;
                this.carrinho$.next(_carrinho); 
                this.setGuid(guid);
                const carrinhos = await this.obterCarrinhos();
                if (carrinhos?.length) {
                    this.setCarrinho({...carrinhos[0]});
                }
            } else if (carrinho.venda !== "avulso" || !carrinho?.avulsos?.length) {
                this.limpar();
                await this.addItemAvulso(item, increment, estadoId);
                return;
            } else {
                if (carrinho?.avulsos) {
                    const componenteId = (item?.componente?.id || item?.componente_id);
                    const itemIndex = Number(carrinho?.avulsos?.findIndex(i => (i?.componente?.id || i?.componente_id) === componenteId ));

                    if (carrinho.avulsos && itemIndex > -1) {
                        if (increment) {
                            carrinho.avulsos[itemIndex].quantidade += item.quantidade;
                        } else {
                            carrinho.avulsos[itemIndex].quantidade = item.quantidade;
                        }
                        await this.atualizarCarrinho(carrinho);
                        return
                    }
                }
                if (!carrinho?.estado_id && estadoId) {
                    carrinho.estado_id = estadoId;
                }
                const avulsos = carrinho?.avulsos || [];
                carrinho["avulsos"] = [...avulsos, item];
                await this.atualizarCarrinho(carrinho);
            }
        } catch(error) {
            if (error instanceof AppError) {
                error.openNotifier();
                return;
            }
        }
    }

    removeItem = async  (indexItem: number): Promise<void> => {
        const carrinho = this.getCarrinho();

        try {
            if (carrinho !== null) {
                if (carrinho?.itens && indexItem !== undefined && indexItem > -1) {
                    carrinho.itens[indexItem]["produto_id"] = carrinho.itens[indexItem].produto?.id;
                    carrinho.itens[indexItem]["@remove"] = true;
                }
                
                await this.atualizarCarrinho(carrinho);
            }
        } catch(error) {
            if (error instanceof AppError) {
                error.openNotifier();
                return;
            }          
        }
    }

    removeItemAvulso = async  (indexItem: number): Promise<void> => {
        const carrinho = this.getCarrinho();

        try {
            if (carrinho !== null) {
                if (carrinho?.avulsos && indexItem !== undefined && indexItem > -1) {
                    carrinho.avulsos[indexItem]["@remove"] = true;
                }
                
                await this.atualizarCarrinho(carrinho, true);
            }
        } catch(error) {
            if (error instanceof AppError) {
                error.openNotifier();
                return;
            }
        }
    }

    limpar() {
        this.carrinho$.next(null);
    }

    get guid() {
        return this.guid$.asObservable();
    }

    setGuid(guid: string) {
        LocalStorage.set("solar.carrinho_guid", guid, true);
        this.guid$.next(guid);
    }

    getGuid() {
        return this.guid$.getValue();
    }

    recuperarGuid(): string {
        const guid = LocalStorage.get("solar.carrinho_guid", true);
        this.guid$.next(guid);
        return guid;
    }

    criarCarrinho = async (carrinho: TCarrinho | null): Promise<string> => {
    
        const _carrinho: TCarrinho = carrinho !== null ? carrinho : this.carrinho$.getValue() || {};

        const request: IDataRequest = {
            url:  `/carrinho/criar`,
            data: _carrinho,
        }

        const response = await apiNoLoading.post(request);

        if (response.statusCode !== STATUS_CODE.OK) {
            throw new AppError('Não foi possível criar o carrinho. Tente novamente.');
        }

        const guid = response?.data?.guid || "";
        this.setGuid(guid);

        return guid;
    }

    atualizarCarrinho = async (carrinho: TCarrinho | null, limparCarrinho?: boolean): Promise<void> => {
        const _carrinho: TCarrinho = carrinho !== null ? carrinho : this.carrinho$.getValue() || {};

        const itensAtualizados = _carrinho.itens?.map((item) => {
            const _item: TCarrinhoItem = {
                sequencia: item.sequencia,
                produto_id: item.produto?.id,
                quantidade: item.quantidade,
                itens_adicionais: item?.itens_adicionais || [],
            }

            if (item["@remove"]) {
                _item["@remove"] = true;
            }

            return _item;
        });
        
        const itensAvulsos = Array.from((_carrinho.avulsos ?? []).reduce((map, item) => {
            const componenteId = item?.componente_id || item?.componente?.id;
            if (!componenteId) return map;
            if (map.has(componenteId)) return map;

            const novoItem: TCarrinhoAvulso = {
                id: item?.id,
                quantidade: item.quantidade,
                componente_id: componenteId,
                valor: item?.valor || 0,
            };

            if (item["@remove"]) {
                novoItem["@remove"] = true;
            }

            if (!item.id) {
                novoItem["@new"] = true;
            }

            map.set(componenteId, novoItem);

            return map;
        }, new Map()).values());

        const payload = carrinho?.venda === 'avulso' 
            ? {
                avulsos: itensAvulsos,
            } : {
                itens: itensAtualizados,
            } 

        const request: IDataRequest = {
            url:  `/carrinho/atualizar/${_carrinho.guid}`,
            data: payload,
        }

        const response = await apiNoLoading.put(request);

        if (response.statusCode !== STATUS_CODE.OK) {
            throw new AppError("Não foi possível atualizar o carrinho. Tente novamente.");
        }

        const carrinhos = await this.obterCarrinhos();
        if (carrinhos?.length) {
            this.setCarrinho({...carrinhos[0]});

            const atual = carrinhos[0];
            const avulso = atual?.venda === 'avulso';
            if (limparCarrinho && avulso && !atual?.avulsos?.length) {
                this.limpar();
            }
        }
    }

    obterCarrinhos = async () => {
        const request: IDataRequest = {
            url: `/carrinho/ultimo?situacao_in=pendente&includes=itens,itens.produto,itens.itens_adicionais,avulsos,avulsos.componente,avulsos.componente.preco&reduce=1`,
        }
        
        const response = await apiNoLoading.get(request);

        if (![ STATUS_CODE.OK, STATUS_CODE.NO_CONTENT].includes(response.statusCode)) {
            // Erro
        }

        return response.data || [];
    }

    finalizar = async (): Promise<TFinalizarCarrinho | undefined> => {
        const carrinho = {...this.getCarrinho()};
        if (carrinho !== null) {
            const request: IDataRequest = {
                url:  `/projetos/carrinhos/integrar/${carrinho.guid}`,
            }
            
            const response: IDataResponse<TFinalizarCarrinho> = await api.post(request);

            if (response.statusCode !== STATUS_CODE.OK) {
                throw new Error('Não foi possível finalizar o carrinho. Tente novamente.');
            }

            this.setCarrinho(null);
            this.setGuid("");
            return  response.data;
        }
    }

    iniciar = async () => {
        const localGuid = this.recuperarGuid();
        const carrinhos = await this.obterCarrinhos();
        if (carrinhos.length) {
            const carrinho = carrinhos[0];

            if (localGuid && localGuid !== carrinho.guid) {
                this.limpar();
                return;
            }

            this.setCarrinho({...carrinho});
            this.setGuid(carrinho.guid || "");
            return;
        }
    }

    
    static valorItensAdicionais =  (item: TCarrinhoItem) => {
        return item?.itens_adicionais?.reduce((total: number, item: TItemAdicional) => {
            total += ((item.valor_com_reducao * item.quantidade) || 0);
            return total;
        }, 0) || 0;
    }

    static valorTotal = (item: TCarrinhoItem, ) => {
        return ((item?.produto?.preco || 0) + CarrinhoStoreClass.valorItensAdicionais(item))
    }

    static valorAvulso = (item: TCarrinhoAvulso, estadoId?: string): number => {
        if (item?.componente?.valor) return item?.componente?.valor;
        if (item?.valor) return item?.valor;
        const precos = item?.componente?.precos || [];
        const precoUf = precos?.find(i => i.estado_id === estadoId);
        return precoUf?.valor || 0;
    }
}

const CarrinhoStore = new CarrinhoStoreClass();

export { CarrinhoStore }